import { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  scheduledDashboardAPI,
  type DemandStabilityData,
} from '../../../lib/api/scheduled-dashboard';
import { DemandMovementChart } from './DemandMovementChart';
import type { DashboardFilterScope } from './shared';

interface DemandStabilityProps {
  filters?: DashboardFilterScope;
}

/**
 * Demand Stability sub-tab (schedule-line installs only).
 *
 * One forward-looking visualisation: the scheduled demand book per delivery
 * week from this week onwards, with the qty added/removed per week since
 * last week's snapshot. Reconstructed server-side so import resync noise
 * nets out. Honours the dashboard's supplier and part filters.
 */
export default function DemandStability({ filters }: DemandStabilityProps) {
  const [data, setData] = useState<DemandStabilityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const customerId = filters?.customer_id;
  const supplierId = filters?.supplier_id;
  const partNumber = filters?.part_number;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    scheduledDashboardAPI
      .getDemandStability({
        customer_id: customerId,
        supplier_id: supplierId,
        part_number: partNumber,
      })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Demand stability fetch failed:', err);
        setError('Could not load demand stability data.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customerId, supplierId, partNumber]);

  if (loading && !data) {
    return (
      <div className="h-64 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="h-64 flex flex-col items-center justify-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-5 w-5" />
        {error}
      </div>
    );
  }

  const fmtDate = (iso: string) =>
    new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          Your forward demand book, this week onwards — and how it moved since
          last week. ERP resync churn nets out; only real movement counts.
        </p>
        <p className="text-xs text-muted-foreground text-right shrink-0">
          Snapshots run weekly, Thursdays early morning.
          {data?.last_captured
            ? <> Last captured: <span className="font-medium text-foreground">{fmtDate(data.last_captured)}</span>.</>
            : <> <span className="font-medium text-amber-700">No snapshot captured yet</span> — baseline is reconstructed.</>}
        </p>
      </div>
      <DemandMovementChart data={data} />
    </div>
  );
}
